import { Box, MenuItem, Paper, Stack, Typography } from "@mui/material";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { FC, useCallback } from "react";
import { TfiHeadphoneAlt } from "react-icons/tfi";
import { HeaderNavLinkItem } from "../../header-menu-utils";

interface DefaultV2DesktopNavLinksProps {
  items: HeaderNavLinkItem[];
}

export const DefaultV2DesktopNavLinks: FC<DefaultV2DesktopNavLinksProps> = ({ items }) => {
  const pathname = usePathname();

  const isLinkActive = useCallback(
    (url?: string) => {
      if (!url) return false;
      return pathname === url || (url === "/" && pathname === "/");
    },
    [pathname],
  );

  if (!items.length) {
    return null;
  }

  return (
    <Box display="flex" gap={3} alignItems="stretch" height="100%" px={1} justifyContent="center">
      {items.map((item) => {
        const hasDropdown = !!item.subs?.length;
        const isItemActive = hasDropdown
          ? item.subs!.some((sub) => isLinkActive(sub.url))
          : isLinkActive(item.url);

        const labelNode = (
          <Typography sx={{ lineHeight: 1.5, fontWeight: isItemActive ? 800 : 700 }}>
            {item.label}
          </Typography>
        );

        return (
          <Box
            key={item.key}
            component={!hasDropdown && item.url ? Link : "div"}
            href={!hasDropdown && item.url ? item.url : undefined}
            display="flex"
            alignItems="center"
            justifyContent="center"
            alignSelf="stretch"
            sx={{
              px: 1.5,
              py: 0,
              textDecoration: "none",
              color: "grey.900",
              borderBottom: "3px solid",
              borderColor: isItemActive ? "primary.main" : "transparent",
              "&:hover": {
                cursor: "pointer",
                color: "grey.900",
                borderColor: "primary.main",
              },
            }}
          >
            {hasDropdown ? (
              <Box
                sx={{
                  position: "relative",
                  display: "inline-flex",
                  alignItems: "center",
                  maxWidth: "100%",
                  "&:hover .default-v2-dd": {
                    opacity: 1,
                    visibility: "visible",
                    pointerEvents: "auto",
                  },
                }}
              >
                {item.url ? (
                  <Typography
                    component={Link}
                    href={item.url}
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      lineHeight: 1.5,
                      fontWeight: isItemActive ? 800 : 700,
                      color: "inherit",
                      textDecoration: "none",
                    }}
                  >
                    {item.label}
                  </Typography>
                ) : (
                  labelNode
                )}
                <Box
                  className="default-v2-dd"
                  sx={{
                    position: "absolute",
                    left: 0,
                    top: "100%",
                    pt: 1.25,
                    minWidth: 220,
                    zIndex: 2000,
                    opacity: 0,
                    visibility: "hidden",
                    pointerEvents: "none",
                    transition: "opacity 0.12s ease, visibility 0.12s ease",
                  }}
                >
                  <Paper elevation={3} sx={{ py: 0.5 }}>
                    {item.subs!.map((sub) => {
                      const isSubActive = isLinkActive(sub.url);
                      return (
                        <MenuItem
                          key={sub.key}
                          component={Link}
                          href={sub.url}
                          sx={{
                            px: 2,
                            py: 1.25,
                            fontWeight: isSubActive ? 800 : 700,
                            color: isSubActive ? "primary.main" : "inherit",
                            "&:hover": {
                              backgroundColor: "primary.light",
                              color: "primary.main",
                            },
                          }}
                        >
                          {sub.label}
                        </MenuItem>
                      );
                    })}
                  </Paper>
                </Box>
              </Box>
            ) : (
              labelNode
            )}
          </Box>
        );
      })}
    </Box>
  );
};

DefaultV2DesktopNavLinks.displayName = "DefaultV2DesktopNavLinks";

export const DefaultV2DesktopHotlineBlock: FC<{ hotline: string | undefined }> = ({ hotline }) => {
  if (!hotline) return null;

  return (
    <Stack direction="row" alignItems="center" alignSelf="stretch" spacing={2} px={0}>
      <Box
        display="flex"
        alignItems="center"
        gap={1.5}
        sx={{
          cursor: "pointer",
          px: 1.5,
          py: 1,
        }}
        onClick={() => {
          window.location.href = `tel:${hotline}`;
        }}
      >
        <TfiHeadphoneAlt size={32} />
        <Stack spacing={0.25}>
          <Typography
            variant="caption"
            fontWeight={700}
            sx={{ color: "text.grey", lineHeight: 1.2 }}
          >
            Hotline
          </Typography>
          <Typography sx={{ color: "text.black", lineHeight: 1.2 }} fontWeight={800}>
            {hotline}
          </Typography>
        </Stack>
      </Box>
    </Stack>
  );
};

DefaultV2DesktopHotlineBlock.displayName = "DefaultV2DesktopHotlineBlock";
